import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import CustomerExperiencePhone from "./CustomerExperiencePhone";
import {
  CUSTOMER_BEATS,
  CUSTOMER_HEADLINE,
  CUSTOMER_PAYOFF,
  CUSTOMER_SCAN_TAG,
  CUSTOMER_STAMP,
  CUSTOMER_SUBHEAD,
} from "./customerExperienceContent";

const CustomerExperienceBand = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 px-6 overflow-hidden"
      data-testid="customer-experience-band"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_15%_50%,rgba(255,95,109,0.08),transparent_65%)] pointer-events-none" />
      <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-white/[0.04] to-transparent hidden lg:block" />

      <div className="max-w-6xl mx-auto relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <CustomerExperiencePhone />
            <p className="mt-4 text-center lg:text-left text-[10px] font-mono text-gray-600 tracking-wide">
              Live intercept · After hours · Real conversation flow
            </p>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="badge-mono text-[10px] font-bold tracking-[0.28em] text-orange-400/90 uppercase">
                {CUSTOMER_STAMP}
              </span>
              <span className="text-[10px] font-mono text-gray-600 px-2.5 py-1 rounded-full border border-white/[0.06] bg-white/[0.02]">
                {CUSTOMER_SCAN_TAG}
              </span>
            </div>

            <h2
              className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#F3F4F6] leading-[1.1] mb-4"
              data-testid="customer-experience-headline"
            >
              {CUSTOMER_HEADLINE.lead}
              <br />
              <span className="text-orange-400">{CUSTOMER_HEADLINE.accent}</span>
            </h2>

            <p className="font-body text-sm sm:text-base text-[#9CA3AF] leading-relaxed max-w-xl mb-8">
              {CUSTOMER_SUBHEAD}
            </p>

            <div className="relative pl-5 sm:pl-6" data-testid="customer-experience-beats">
              <div className="absolute left-[7px] sm:left-[9px] top-3 bottom-3 w-px bg-gradient-to-b from-orange-500/30 via-orange-500/15 to-teal-500/30" />

              <div className="space-y-6 sm:space-y-7">
                {CUSTOMER_BEATS.map((beat, index) => {
                  const isHero = beat.highlight;

                  return (
                    <motion.div
                      key={beat.id}
                      initial={reduceMotion ? false : { opacity: 0, x: 12 }}
                      animate={
                        reduceMotion || isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 12 }
                      }
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className={`relative flex gap-4 sm:gap-5 ${
                        isHero ? "sm:-ml-1" : ""
                      }`}
                      data-testid={`customer-experience-beat-${index}`}
                    >
                      <div
                        className={`absolute -left-5 sm:-left-6 top-1.5 w-[15px] h-[15px] rounded-full border-2 flex-shrink-0 ${
                          isHero
                            ? "border-orange-400 bg-orange-500/30 shadow-[0_0_12px_rgba(249,115,22,0.45)]"
                            : "border-white/20 bg-[#0c1018]"
                        }`}
                      />

                      <div
                        className={`flex-1 min-w-0 rounded-xl border px-4 py-3.5 sm:px-5 sm:py-4 ${
                          isHero
                            ? "border-orange-500/35 bg-gradient-to-r from-orange-500/12 to-transparent"
                            : "border-white/[0.06] bg-white/[0.015]"
                        }`}
                      >
                        {isHero && (
                          <span className="badge-mono text-[9px] text-orange-400 tracking-wider mb-1.5 block">
                            KEY MOMENT
                          </span>
                        )}

                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span
                            className={`font-mono font-bold leading-none ${
                              isHero
                                ? "text-3xl sm:text-4xl text-orange-400"
                                : beat.id === "outcome"
                                  ? "text-2xl sm:text-3xl text-teal-400"
                                  : "text-2xl sm:text-3xl text-white"
                            }`}
                          >
                            {beat.value}
                          </span>
                          <span className="font-heading font-bold text-sm sm:text-base text-[#F3F4F6]">
                            {beat.label}
                          </span>
                        </div>
                        <p className="text-[11px] sm:text-xs text-gray-500 font-body mt-1.5">
                          {beat.detail}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <div
              className="mt-8 flex items-center gap-3 pl-5 sm:pl-6"
              data-testid="customer-experience-payoff"
            >
              <div className="h-px flex-1 max-w-8 bg-orange-500/40" />
              <p className="font-heading font-semibold text-sm sm:text-base text-[#F3F4F6] leading-snug">
                {CUSTOMER_PAYOFF.prefix}{" "}
                <span className="text-orange-400">{CUSTOMER_PAYOFF.highlight}</span>{" "}
                {CUSTOMER_PAYOFF.suffix}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerExperienceBand;
